import { useLayoutEffect, useState, useSyncExternalStore, type RefObject } from 'react';
import {
  getNoticeStackGeometry,
  noticeStackClearance,
  setNoticeStackRoomRequest,
  subscribeNoticeStackGeometry,
} from '@/lib/app-notice-stack';

/**
 * The stack's clearance (`noticeStackClearance`): the first viewport row clear
 * of the app notice stack while a notice shows, 0 otherwise. Sheets and
 * dialogs that paint below the stack keep their top at or below it (SheetShell,
 * the testimony dialog, the verse panel); the Bible chapter's sticky header
 * sticks there. Reads the DISPLAYED band only, so it cannot feed the Home cap.
 */
export function useNoticeStackClearance(): number {
  const geometry = useSyncExternalStore(subscribeNoticeStackGeometry, getNoticeStackGeometry, () => null);
  return noticeStackClearance(geometry);
}

/**
 * The height a full-screen surface reserves below its own header so that its
 * search row, filters, tabs, scroll container and prayer controls start below
 * the app notice stack (v0.2.0 finishing, item 1).
 *
 * `AppNoticeStack` is fixed at the top of the viewport (z-index 495) and every
 * PrayPage overlay paints inside PrayPage's isolated stacking context, below
 * it. Each surface therefore renders an in-flow spacer directly below its
 * header, with the returned callback ref on that spacer, and gives it the
 * returned height. The spacer's flow position (its offset parent's top plus
 * its `offsetTop`: chrome geometry that no scroll inside the surface moves) is
 * the header's bottom edge, so the result is `clearance - header bottom`, never
 * negative and 0 while no notice shows. It reads the stack's DISPLAYED band
 * through `noticeStackClearance` (the home cap respected, 8 px added when
 * capped), never the uncapped height, so it cannot feed the cap back.
 *
 * The ref is a callback ref: the measurement follows the spacer element that
 * is actually mounted, so a surface with more than one render path (a loading
 * shell and the page, the Bible chapter's invalid-input fallback and reader)
 * passes the same ref to each path's spacer and the flow top is re-measured
 * whenever the mounted spacer changes.
 *
 * Route pages (Settings, the Bible index, chapter and Journal) scroll the
 * document, so their spacer's position is taken at window scroll 0 (the window
 * scroll is added back whenever the offset parent is not itself fixed). The
 * reserve is then the same at every scroll position: the page starts below the
 * notices and scrolling moves content under them, as on any page with fixed
 * notices.
 *
 * Nothing about the notices changes: their slots, live regions, order, z and
 * dismissal are untouched; the surface only starts lower.
 */
export function useNoticeStackReserve(): [number, (element: HTMLElement | null) => void] {
  const clearance = useNoticeStackClearance();
  // The mounted spacer; the state setter is the callback ref (stable identity).
  const [spacer, setSpacer] = useState<HTMLElement | null>(null);
  const [flowTop, setFlowTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    const parent = spacer?.offsetParent;
    // No laid-out spacer: nothing to measure. A spacer mounted later (another
    // render path) re-runs this effect with its own element.
    if (!spacer || !(parent instanceof HTMLElement)) return;
    let frame = 0;
    // A fixed overlay root never moves; a route page's offset parent moves with
    // the document scroll, so its position is taken at window scroll 0.
    const fixedParent = getComputedStyle(parent).position === 'fixed';
    const measure = () => {
      frame = 0;
      const next = parent.getBoundingClientRect().top + spacer.offsetTop + (fixedParent ? 0 : window.scrollY);
      setFlowTop((current) => (current !== null && Math.abs(current - next) < 0.5 ? current : next));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    // The spacer's top moves only with what precedes it in flow (its header)
    // or with the surface itself; the stack's own changes arrive through the
    // geometry subscription.
    const sizes = new ResizeObserver(schedule);
    sizes.observe(parent);
    const header = spacer.previousElementSibling;
    if (header) sizes.observe(header);
    const vv = window.visualViewport;
    window.addEventListener('resize', schedule);
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);
    measure();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      sizes.disconnect();
      window.removeEventListener('resize', schedule);
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
    };
  }, [spacer]);

  if (clearance <= 0 || flowTop === null) return [0, setSpacer];
  return [Math.max(0, Math.ceil(clearance - flowTop)), setSpacer];
}

/** The gap kept between the notices and the field, and between the field and the action row. */
const KEYBOARD_ROOM_GAP = 8;
const NON_TEXT_INPUTS = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

function isTextField(element: Element | null): element is HTMLTextAreaElement | HTMLInputElement {
  if (element instanceof HTMLTextAreaElement) return true;
  return element instanceof HTMLInputElement && !NON_TEXT_INPUTS.has(element.type);
}

/** Two lines of the field's text plus its padding and border: the least a limited text box keeps. */
function twoLineHeight(field: HTMLElement): number {
  const style = getComputedStyle(field);
  const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
  return Math.ceil(
    2 * lineHeight +
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) +
      parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth),
  );
}

/**
 * Keyboard room (v0.2.0 follow-through: a small phone, Extra Large text, the
 * app notices and the soft keyboard). A sheet or dialog passes `active` while
 * its soft keyboard is open AND a notice shows, and `scroller`, its scroll
 * area, which then sets `data-keyboard-room` so that its action row (the
 * `[data-keyboard-actions]` element inside it) is sticky at the bottom of the
 * scroll area, directly above the keyboard. While active:
 *
 * 1. Room. The surface asks the notice stack for the room its text field and
 *    action row need between the stack's clearance and the keyboard's top
 *    edge: the field's unconstrained height, the action row's height and two
 *    8 px gaps (`setNoticeStackRoomRequest`). The stack becomes compact only
 *    if its band leaves less (it then scrolls inside itself, never below
 *    48 px); every notice stays mounted, in its order and reachable.
 * 2. Field height. If even the compact stack leaves too little, the focused
 *    text box is limited to the band between the notices and the action row,
 *    never below two lines of its text. If that band cannot hold two lines
 *    either (the smallest phones), the hook sets `data-keyboard-short` on the
 *    scroll area: the row is then not sticky, so it never covers the words
 *    being typed, and it is reached by scrolling.
 * 3. Field in view. When the room itself changes (the clearance or the
 *    keyboard's height) or the focus moves to a field, the scroll area is
 *    scrolled, only as far as needed, so the focused field sits whole in that
 *    band. Nothing else moves the scroll area: not the person's own scrolling
 *    (on the smallest phones they scroll down to Save and Cancel with the
 *    keyboard open), not the visual viewport panning, and not a resize of the
 *    action row, such as the save cue appearing in it.
 *
 * The field is the focused text field inside `scroller`, else its first text
 * field (for the room request only). Everything is undone when `active` turns
 * false or the surface unmounts: the request is withdrawn, so the stack
 * returns to its size, and the field's limit is removed.
 */
export function useNoticeStackKeyboardRoom(active: boolean, scrollerRef: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!active || !scroller) return;
    const owner = {};
    let frame = 0;
    // The text box whose height this hook limits (inline max/min height).
    let limited: HTMLElement | null = null;
    let observedActions: HTMLElement | null = null;
    // The room the field was last placed in, and whether the focus has moved
    // since: only a change of these re-positions the field (point 3).
    let placedKeyboard = -1;
    let placedClearance = -1;
    let focusMoved = true;
    const release = () => {
      if (!limited) return;
      limited.style.removeProperty('max-height');
      limited.style.removeProperty('min-height');
      limited = null;
    };
    // The field's height without this hook's limit (read synchronously, no paint between).
    const unconstrainedHeight = (field: HTMLElement) => {
      if (field !== limited) return field.offsetHeight;
      const max = field.style.getPropertyValue('max-height');
      const min = field.style.getPropertyValue('min-height');
      field.style.removeProperty('max-height');
      field.style.removeProperty('min-height');
      const height = field.offsetHeight;
      field.style.setProperty('max-height', max);
      field.style.setProperty('min-height', min);
      return height;
    };
    const sizes = new ResizeObserver(() => schedule());
    const update = () => {
      frame = 0;
      const focused = document.activeElement;
      const focusedField = isTextField(focused) && scroller.contains(focused) ? focused : null;
      if (limited && limited !== focusedField) release();
      const field = focusedField
        ?? [...scroller.querySelectorAll('textarea, input')].find((element) => isTextField(element)) as HTMLElement | undefined
        ?? null;
      const actions = scroller.querySelector<HTMLElement>('[data-keyboard-actions]');
      if (actions !== observedActions) {
        if (observedActions) sizes.unobserve(observedActions);
        if (actions) sizes.observe(actions);
        observedActions = actions;
      }
      const actionsHeight = actions ? actions.offsetHeight : 0;
      if (!field) {
        setNoticeStackRoomRequest(owner, null);
        return;
      }
      const natural = unconstrainedHeight(field);
      setNoticeStackRoomRequest(owner, Math.ceil(natural + actionsHeight + 2 * KEYBOARD_ROOM_GAP));
      if (!focusedField) {
        scroller.removeAttribute('data-keyboard-short');
        return;
      }
      // The band the focused field must sit in: below the notices' clearance
      // (and the scroll area's own top), above the action row stuck at the
      // bottom of the scroll area, which ends at the keyboard's top edge.
      const vv = window.visualViewport;
      const visibleBottom = vv ? vv.height + vv.offsetTop : window.innerHeight;
      const box = scroller.getBoundingClientRect();
      // The scroll area's visible bottom: the keyboard's top edge.
      const scrollBottom = Math.min(box.bottom, visibleBottom);
      const clearance = noticeStackClearance(getNoticeStackGeometry());
      const keyboard = vv ? window.innerHeight - vv.height : 0;
      const reposition = focusMoved || keyboard !== placedKeyboard || clearance !== placedClearance;
      focusMoved = false;
      placedKeyboard = keyboard;
      placedClearance = clearance;
      const bandTop = Math.max(box.top, clearance) + KEYBOARD_ROOM_GAP;
      const stuckBandBottom = scrollBottom - actionsHeight - KEYBOARD_ROOM_GAP;
      const minimum = focusedField instanceof HTMLTextAreaElement ? twoLineHeight(focusedField) : natural;
      // Too short for the field AND the row even with the compact stack (the
      // smallest phones): the row is not stuck (`data-keyboard-short`), so it
      // never covers the words being typed; it stays below, reached by
      // scrolling. The row keeps its height either way, so this cannot flap.
      const short = actionsHeight > 0 && stuckBandBottom - bandTop < minimum - 0.5;
      if (short) scroller.setAttribute('data-keyboard-short', '');
      else scroller.removeAttribute('data-keyboard-short');
      const bandBottom = short ? scrollBottom - KEYBOARD_ROOM_GAP : stuckBandBottom;
      const band = bandBottom - bandTop;
      if (focusedField instanceof HTMLTextAreaElement && natural > band + 0.5) {
        const height = `${Math.max(minimum, Math.floor(band))}px`;
        focusedField.style.setProperty('max-height', height);
        focusedField.style.setProperty('min-height', '0px');
        limited = focusedField;
      } else {
        release();
      }
      const rect = focusedField.getBoundingClientRect();
      let delta = 0;
      if (rect.top < bandTop) delta = rect.top - bandTop;
      else if (rect.bottom > bandBottom) delta = Math.min(rect.bottom - bandBottom, rect.top - bandTop);
      if (reposition && Math.abs(delta) >= 1) scroller.scrollTop += delta;
    };
    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }
    const unsubscribe = subscribeNoticeStackGeometry(schedule);
    const vv = window.visualViewport;
    window.addEventListener('resize', schedule);
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);
    const onFocusIn = () => {
      focusMoved = true;
      schedule();
    };
    scroller.addEventListener('focusin', onFocusIn);
    update();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      unsubscribe();
      sizes.disconnect();
      window.removeEventListener('resize', schedule);
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      scroller.removeEventListener('focusin', onFocusIn);
      scroller.removeAttribute('data-keyboard-short');
      release();
      setNoticeStackRoomRequest(owner, null);
    };
  }, [active, scrollerRef]);
}
